/**
 * Intents tab: intent CRUD + intent texts + sync to Qdrant.
 * Unified accordion layout: each intent is one card with metadata and texts together.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  hubService,
  type IntentSummary,
  type CreateIntentDto,
  type UpdateIntentDto,
  type IntentText,
  type IntentSyncStatus,
} from '../../services/hub.service';
import {
  canChangePhrase,
  canCreateIntent,
  CREATE_HINT,
  filterPhrases,
  intentLocks,
  SOURCE_LABEL,
  SYNCED_HINT,
  type PhraseSource,
} from '../../lib/intentView';
import { actionLabel, missingFor } from '../../lib/onMatchView';
import { OnMatchEditor } from './OnMatchEditor';
import { flowService, type Flow } from '../../services/flow.service';
import { CyrillicNote } from '../quality/CyrillicNote';

const SYSTEM = 'chatbot';

export interface IntentsTabProps {
  botId: string;
  system?: string;
}

export function IntentsTab({ botId, system = SYSTEM }: IntentsTabProps) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<IntentSummary[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<IntentSummary | null>(null);
  const [saving, setSaving] = useState(false);
  const [stale, setStale] = useState<Record<string, string[]>>({});
  const isV2 = system === 'chatbot_v2';
  const [form, setForm] = useState<CreateIntentDto>({
    system,
    botId,
    intentCode: '',
    domain: '',
    name: '',
    description: '',
    workflowId: '',
    confidenceThreshold: undefined,
  });

  useEffect(() => {
    load();
  }, [botId]);
  useEffect(() => {
    flowService.listFlows(botId, undefined, getToken()).then(setFlows).catch(() => setFlows([]));
  }, [botId, getToken]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listIntentSummaries(botId, system, token);
      setItems(data);
      if (system === 'chatbot_v2') {
        // Informational: a failure just leaves stale marks off.
        hubService.staleBlocks(botId, token).then((r) => setStale(r.stale)).catch(() => setStale({}));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setCreating(true);
    setEditing(null);
    setForm({ system, botId, intentCode: '', domain: '', name: '', description: '', workflowId: '', confidenceThreshold: undefined });
  }

  function openEdit(item: IntentSummary) {
    setEditing(item);
    setCreating(false);
    setForm({
      system,
      botId,
      intentCode: item.intentCode,
      domain: item.domain ?? '',
      name: item.name,
      description: item.description ?? '',
      workflowId: item.workflowId ?? '',
      confidenceThreshold: item.confidenceThreshold ?? undefined,
    });
  }

  function closeForm() {
    setCreating(false);
    setEditing(null);
    setForm({ system, botId, intentCode: '', domain: '', name: '', description: '', workflowId: '', confidenceThreshold: undefined });
  }

  async function handleCreate() {
    if (!form.intentCode.trim() || !form.name.trim()) return;
    setSaving(true);
    try {
      const token = getToken();
      await hubService.createIntent(
        {
          ...form,
          botId,
          description: form.description || undefined,
          workflowId: form.workflowId || undefined,
          confidenceThreshold: form.confidenceThreshold,
        },
        token,
      );
      closeForm();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    if (!editing) return;
    setSaving(true);
    try {
      const token = getToken();
      const dto: UpdateIntentDto = {
        domain: form.domain || undefined,
        // A synced intent's name belongs to chatbot; the server rejects a change, so never send it.
        name: intentLocks(editing).name ? undefined : form.name,
        description: form.description || undefined,
        workflowId: form.workflowId || undefined,
        confidenceThreshold: form.confidenceThreshold,
      };
      await hubService.updateIntent(botId, editing.intentCode, dto, system, token);
      closeForm();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(intentCode: string) {
    if (!confirm(`Delete intent "${intentCode}"?`)) return;
    try {
      const token = getToken();
      await hubService.deleteIntent(botId, intentCode, system, token);
      closeForm();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  const flowMap = Object.fromEntries(flows.map((f) => [f.id, f.name]));

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Intents define routing and workflows. Add example phrases per intent for classification.
        </p>
        {!creating && (
          <button
            onClick={openCreate}
            disabled={!canCreateIntent(system)}
            title={canCreateIntent(system) ? undefined : CREATE_HINT}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Intent
          </button>
        )}
      </div>
      {!canCreateIntent(system) && (
        <p className="-mt-4 text-xs text-gray-500">{CREATE_HINT}. Phrases you add here stay in hub.</p>
      )}

      {creating && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm space-y-4">
          <h4 className="text-sm font-medium text-gray-800">New intent</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Intent code</label>
              <input
                placeholder="e.g. track_parcel"
                value={form.intentCode}
                onChange={(e) => setForm((f) => ({ ...f, intentCode: e.target.value }))}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              <input
                placeholder="Display name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
              <textarea
                placeholder="For LLM context (helps intent classification)"
                value={form.description ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Domain</label>
              <input
                placeholder="e.g. domain.flow"
                value={form.domain}
                onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="text-[11px] text-gray-400 mt-0.5">For system routing</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Workflow</label>
              <select
                value={form.workflowId}
                onChange={(e) => setForm((f) => ({ ...f, workflowId: e.target.value }))}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">None</option>
                {flows.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Confidence threshold</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                placeholder="0.7 (default)"
                value={form.confidenceThreshold ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({ ...f, confidenceThreshold: v === '' ? undefined : parseFloat(v) }));
                }}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="text-[11px] text-gray-400 mt-0.5">Intent-specific (0–1). Empty = system default</p>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleCreate}
              disabled={saving || !form.intentCode.trim() || !form.name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Create'}
            </button>
            <button onClick={closeForm} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 && !creating ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">
            {canCreateIntent(system)
              ? 'No intents yet. Click Add Intent to create one.'
              : 'No intents in hub yet. This bot has not been imported from chatbot.'}
          </div>
        ) : (
          items.map((item) => (
            <IntentCard
              key={item.id}
              intent={item}
              flowMap={flowMap}
              getToken={getToken}
              isEditing={editing?.id === item.id}
              form={form}
              setForm={setForm}
              onEdit={() => openEdit(item)}
              onDelete={() => handleDelete(item.intentCode)}
              onCloseEdit={closeForm}
              onUpdate={handleUpdate}
              onError={(msg) => setError(msg)}
              saving={saving}
              botId={botId}
              system={system}
              isV2={isV2}
              staleChannels={missingFor(item.id, stale)}
              onChanged={load}
            />
          ))
        )}
      </div>
    </div>
  );
}

function IntentCard({
  intent,
  flowMap,
  getToken,
  isEditing,
  form,
  setForm,
  onEdit,
  onDelete,
  onCloseEdit,
  onUpdate,
  onError,
  saving,
  botId,
  system,
  isV2,
  staleChannels,
  onChanged,
}: {
  intent: IntentSummary;
  flowMap: Record<string, string>;
  getToken: () => string | null;
  isEditing: boolean;
  form: CreateIntentDto;
  setForm: React.Dispatch<React.SetStateAction<CreateIntentDto>>;
  onEdit: () => void;
  onDelete: () => void;
  onCloseEdit: () => void;
  onUpdate: () => Promise<void>;
  onError: (msg: string) => void;
  saving: boolean;
  botId: string;
  system: string;
  isV2: boolean;
  staleChannels: string[];
  onChanged: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [texts, setTexts] = useState<IntentText[]>([]);
  const [syncStatus, setSyncStatus] = useState<IntentSyncStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState<PhraseSource | 'all'>('all');
  const locks = intentLocks(intent);
  const [editingAction, setEditingAction] = useState(false);

  useEffect(() => {
    if (isEditing) setExpanded(true);
  }, [isEditing]);

  useEffect(() => {
    if (expanded) load();
  }, [expanded, intent.id]);

  async function load() {
    setLoading(true);
    try {
      const token = getToken();
      const [textsData, statusData] = await Promise.all([
        hubService.listIntentTexts(intent.id, token),
        hubService.getIntentTextsSyncStatus(intent.id, token),
      ]);
      setTexts(textsData);
      setSyncStatus(statusData);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Failed to load intent texts');
    } finally {
      setLoading(false);
    }
  }

  async function handleAddText() {
    if (!newText.trim()) return;
    setAdding(true);
    try {
      const token = getToken();
      await hubService.createIntentText(intent.id, newText.trim(), token);
      setNewText('');
      load();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Failed to add text');
    } finally {
      setAdding(false);
    }
  }

  async function handleDeleteText(textId: string) {
    if (!confirm('Delete this example?')) return;
    try {
      const token = getToken();
      await hubService.deleteIntentText(intent.id, textId, token);
      load();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Failed to delete text');
    }
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const token = getToken();
      await hubService.syncIntentToQdrant(intent.id, token);
      load();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <div className="px-4 py-3 flex items-center justify-between gap-4 bg-gray-50 border-b border-gray-200">
        <button
          onClick={() => setExpanded((e) => !e)}
          className="flex-1 flex items-center gap-3 text-left min-w-0"
        >
          <span className="text-gray-400 shrink-0">{expanded ? '▼' : '▶'}</span>
          <span className="font-mono text-sm font-medium text-gray-800 truncate">{intent.intentCode}</span>
          <span className="text-sm text-gray-600 truncate hidden sm:inline">{intent.name}</span>
          {intent.synced && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 shrink-0" title={SYNCED_HINT}>
              from chatbot
            </span>
          )}
          {intent.templateKey && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0 hidden md:inline">
              general: {intent.templateKey}
            </span>
          )}
          <span className="text-[10px] text-gray-500 shrink-0 hidden md:inline">
            {SOURCE_LABEL.rasa} {intent.counts.rasa} · {SOURCE_LABEL.operator} {intent.counts.operator} · review {intent.counts.review}
          </span>
          {isV2 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 shrink-0" title="What v2 sends on a match">
              on match: {actionLabel(intent.onMatch)}
            </span>
          )}
          {staleChannels.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 shrink-0">
              block missing ({staleChannels.join(', ')})
            </span>
          )}
          {intent.unsynced > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">
              {intent.unsynced} not indexed
            </span>
          )}
          <span className="text-xs text-gray-400 truncate hidden md:inline">{intent.domain || '-'}</span>
          <span className="text-xs text-gray-400 truncate hidden lg:inline">
            {intent.workflowId ? flowMap[intent.workflowId] ?? intent.workflowId : '-'}
          </span>
          {syncStatus && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 shrink-0">
              {syncStatus.synced}/{syncStatus.total} synced
            </span>
          )}
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {!isEditing ? (
            <>
              <button onClick={onEdit} className="px-2 py-1 text-xs text-blue-600 hover:underline">
                Edit
              </button>
              {isV2 && (
                <button onClick={() => setEditingAction((v) => !v)} className="px-2 py-1 text-xs text-indigo-600 hover:underline">
                  On match
                </button>
              )}
              {!locks.delete && (
                <button onClick={onDelete} className="px-2 py-1 text-xs text-red-600 hover:underline">
                  Delete
                </button>
              )}
            </>
          ) : null}
        </div>
      </div>

      {isV2 && editingAction && (
        <div className="p-4 bg-white border-b border-gray-200">
          <OnMatchEditor
            botId={botId}
            intentCode={intent.intentCode}
            value={intent.onMatch}
            onSaved={() => {
              setEditingAction(false);
              onChanged();
            }}
            onCancel={() => setEditingAction(false)}
          />
        </div>
      )}
      {expanded && (
        <div className="p-4 bg-white space-y-4">
          {isEditing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={locks.name}
                    title={locks.name ? SYNCED_HINT : undefined}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Domain</label>
                  <input
                    value={form.domain}
                    onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                  <textarea
                    placeholder="For LLM context (helps intent classification)"
                    value={form.description ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Workflow</label>
                  <select
                    value={form.workflowId}
                    onChange={(e) => setForm((f) => ({ ...f, workflowId: e.target.value }))}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">None</option>
                    {Object.entries(flowMap).map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Confidence threshold</label>
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    placeholder="0.7 (default)"
                    value={form.confidenceThreshold ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, confidenceThreshold: v === '' ? undefined : parseFloat(v) }));
                    }}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onUpdate()}
                  disabled={saving || !form.name.trim()}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button onClick={onCloseEdit} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-2 flex-wrap">
                <input
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  placeholder="Add example phrase for intent detection"
                  className="flex-1 min-w-[200px] rounded border border-gray-300 px-3 py-2 text-sm"
                />
                <button
                  onClick={handleAddText}
                  disabled={adding || !newText.trim()}
                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
                >
                  {adding ? 'Adding...' : 'Add'}
                </button>
                <button
                  onClick={handleSync}
                  disabled={syncing || (syncStatus?.pending ?? 0) === 0}
                  className="px-3 py-2 text-sm bg-green-600 text-white rounded disabled:opacity-50"
                  title="Sync to Qdrant"
                >
                  {syncing ? 'Syncing...' : 'Sync'}
                </button>
              </div>
              <CyrillicNote system={system} botId={botId} text={newText} onFix={setNewText} />
              {loading ? (
                <div className="text-sm text-gray-500">Loading...</div>
              ) : (
                <>
                  <div role="tablist" className="flex gap-1 flex-wrap text-xs">
                    {(['all', 'rasa', 'operator', 'review'] as const).map((f) => (
                      <button
                        key={f}
                        role="tab"
                        aria-selected={filter === f}
                        onClick={() => setFilter(f)}
                        className={`px-2 py-0.5 rounded border ${filter === f ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                      >
                        {f === 'all' ? 'All' : SOURCE_LABEL[f]}
                      </button>
                    ))}
                  </div>
                  <ul className="space-y-1 max-h-48 overflow-y-auto">
                    {filterPhrases(texts, filter).map((t) => (
                      <li key={t.id} className="flex items-center justify-between text-sm py-1 gap-2">
                        <span className="text-gray-700 truncate flex-1">{t.text}</span>
                        <span className="text-[10px] px-1.5 rounded shrink-0 bg-gray-100 text-gray-600">
                          {SOURCE_LABEL[t.source ?? 'operator']}
                        </span>
                        <span className={`text-[10px] px-1.5 rounded shrink-0 ${t.syncedToVector ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {t.syncedToVector ? 'indexed' : 'not indexed'}
                        </span>
                        {canChangePhrase(t) ? (
                          <button
                            onClick={() => handleDeleteText(t.id)}
                            className="text-red-600 hover:underline text-xs shrink-0"
                          >
                            Delete
                          </button>
                        ) : (
                          <span className="text-xs shrink-0 text-gray-400" title={SYNCED_HINT} aria-label={SYNCED_HINT}>
                            🔒
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {!loading && texts.length === 0 && (
                <p className="text-sm text-gray-500">No example phrases. Add some for intent detection.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
